import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import {
  Title,
  Filter,
  Field,
  DateRange,
  Pagination,
  Loader,
  Text,
  Image,
} from "components/commons";
import DownloadCard from "components/commons/download-card/download-card";
import { useApi, useMount } from "hooks";
import { getReportList } from "apis";
import { UserContext } from "contexts";
import { ReportStatus } from "enums";
import { GuyWithMagGlass } from "images";
import locale from "localization";
import styles from "./downloads.module.scss";

const DownloadsModule = () => {
  const { user } = useContext(UserContext);

  const isPolling = useRef(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [startEndDate, setStartEndDate] = useState({ startDate: null, endDate: null });

  const { request: fetchReports, loading, result } = useApi({
    api: getReportList,
    handleOwnError: true,
  });

  const loadReports = useCallback(
    async ({ p = page, startDate = startEndDate.startDate, endDate = startEndDate.endDate } = {}) => {
      await fetchReports({
        page: p,
        perPage,
        userId: user?.userId,
        reportTypes: "price-buildup,station-list,brand,station-pricing",
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      });
      isPolling.current = false;
    },
    [fetchReports, page, perPage, user?.userId, startEndDate],
  );

  useMount(() => {
    loadReports({ p: 1 });
  });

  const reports = useMemo(() => result?.reports || [], [result?.reports]);
  const count = result?.count || 0;

  useEffect(() => {
    const hasInProgress = reports.some((r) => r.status === ReportStatus.InProgress);
    if (!hasInProgress) return;
    const interval = setInterval(() => {
      isPolling.current = true;
      loadReports({ p: page });
    }, 5000);
    return () => clearInterval(interval);
  }, [reports, loadReports, page]);

  const handleApplyFilter = useCallback(() => {
    setPage(1);
    loadReports({ p: 1 });
  }, [loadReports]);

  const handleChangePageSize = useCallback(
    (newPerPage) => {
      setPerPage(newPerPage);
      setPage(1);
      fetchReports({
        page: 1,
        perPage: newPerPage,
        userId: user?.userId,
        reportTypes: "price-buildup,station-list,brand,station-pricing",
        ...(startEndDate.startDate ? { startDate: startEndDate.startDate } : {}),
        ...(startEndDate.endDate ? { endDate: startEndDate.endDate } : {}),
      });
    },
    [fetchReports, user?.userId, startEndDate],
  );

  const handleClearFilter = useCallback(() => {
    setStartEndDate({ startDate: null, endDate: null });
    setPage(1);
    fetchReports({
      page: 1,
      perPage,
      userId: user?.userId,
      reportTypes: "price-buildup,station-list,brand,station-pricing",
    });
  }, [fetchReports, perPage, user?.userId]);

  const handleChangePage = useCallback(
    (p) => {
      setPage(p);
      loadReports({ p });
    },
    [loadReports],
  );

  return (
    <>
      <Title>{locale.downloads || "Downloads"}</Title>

      <div className={styles.container}>
        <Filter submit={handleApplyFilter} clear={handleClearFilter}>
          <Field
            label={locale.dateOfGeneration || "Date of Generation"}
            labelPosition="left"
            childrenClassName={styles.fieldContent}
            className={styles.fieldLabel}
          >
            <DateRange
              value={startEndDate}
              onChange={(_, { value }) => setStartEndDate(value)}
            />
          </Field>
        </Filter>
      </div>

      <div style={{ padding: "0 10%" }}>
        {loading && !isPolling.current ? (
          <Loader open={loading} />
        ) : reports.length === 0 ? (
          <div className={styles.noSearchResultRender}>
            <div>
              <Image src={GuyWithMagGlass} />
              <Title small>{locale.sorryNoResultFound || "Sorry, no results found."}</Title>
              <Text subtitle>
                {locale.weCouldNotFindAMatchForSearchCriteria ||
                  "We could not find a match for your search criteria."}
              </Text>
              <Text subtitle>
                {locale.pleaseTryADifferentOne || "Please try a different one."}
              </Text>
            </div>
          </div>
        ) : (
          reports.map((data, key) => (
            <DownloadCard
              key={key}
              data={data}
              reloadData={() => loadReports({ p: page })}
            />
          ))
        )}
      </div>

      <Pagination
        page={page}
        pageSize={perPage}
        dataCount={count}
        onChangePage={handleChangePage}
        onChangePageSize={handleChangePageSize}
      />
    </>
  );
};

export default DownloadsModule;
